import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
//
// Indigo actions, sky-blue highlights and cool paper surfaces.
const PRIMARY = {
  lighter: '#EEEDFF',
  light: '#8C85F5',
  main: '#4640DE',
  dark: '#352FB8',
  darker: '#282378',
};
const SECONDARY = {
  lighter: '#EAF6FF',
  light: '#7BC7FC',
  main: '#1687CC',
  dark: '#106596',
  darker: '#0B4568',
};
const INFO = {
  lighter: '#E5F2FB',
  light: '#6BB8E8',
  main: '#1878B8',
  dark: '#115A8A',
  darker: '#0B3C5D',
};
const SUCCESS = {
  lighter: '#E4F4E8',
  light: '#67C184',
  main: '#157F3C',
  dark: '#0F6130',
  darker: '#0A4320',
};
const WARNING = {
  lighter: '#FDF3E3',
  light: '#F0B861',
  main: '#B25E09',
  dark: '#8A4706',
  darker: '#5C2F04',
};
const ERROR = {
  lighter: '#FBE9E7',
  light: '#E8827C',
  main: '#C0392B',
  dark: '#962C21',
  darker: '#681E16',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F8F8FD',
  200: '#F1F2F8',
  300: '#E3E5ED',
  400: '#C2C6D3',
  500: '#8E8E8E',
  600: '#6E7588',
  700: '#515B70',
  800: '#323B51',
  900: '#25324B',
  '500_8': alpha('#8E8E8E', 0.08),
  '500_12': alpha('#8E8E8E', 0.12),
  '500_16': alpha('#8E8E8E', 0.16),
  '500_24': alpha('#8E8E8E', 0.24),
  '500_32': alpha('#8E8E8E', 0.32),
  '500_48': alpha('#8E8E8E', 0.48),
  '500_56': alpha('#8E8E8E', 0.56),
  '500_80': alpha('#8E8E8E', 0.8),
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: '#fff' },
  warning: { ...WARNING, contrastText: '#fff' },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: '#E6E8EF',
  action: {
    hover: GREY['500_8'],
    selected: GREY['500_16'],
    disabled: GREY['500_80'],
    disabledBackground: GREY['500_24'],
    focus: GREY['500_24'],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light' as const,
    text: { primary: GREY[900], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[100] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark' as const,
    text: { primary: '#fff', secondary: GREY[400], disabled: GREY[500] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY['500_16'] },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;
